import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import NavBar from "./NavBar";
import ChartPage from "./ChartPage";
import PythonPage from "./PythonPage";
import BacktestPage from "./BacktestPage";
import MarketDepthPage from "./MarketDepthPage";
import { SECTIONS } from "./sections";
import { pageStyles, palette } from "./pageStyles";
import { commerceRequest } from "./commerceApi";

const tabStyle = {
  position: "absolute",
  inset: 0,
  minHeight: 0,
  overflow: "hidden",
  flexDirection: "column",
};

const PLAN_RANK = { free: 0, starter: 1, pro: 2, quant: 3, professional: 4 };
const SECTION_PLAN = { python: "pro", backtest: "pro", marketdepth: "professional" };

function PlanRequired({ plan, checking = false }) {
  const name = plan === "professional" ? "Professional" : "Pro";
  return <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 20, color: palette.text }}>
    <div style={{ maxWidth: 430, padding: 22, border: `1px solid ${palette.border}`, borderRadius: 14, background: palette.glass, textAlign: "center" }}>
      <div style={{ color: palette.accentBlue, fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", fontWeight: 700 }}>Plan access</div>
      <h2 style={{ fontSize: 20, margin: "8px 0" }}>{checking ? "Checking your plan" : `${name} plan required`}</h2>
      <p style={{ color: palette.mutedText, fontSize: 13, lineHeight: 1.55, margin: "0 0 14px" }}>{checking ? "Loading the subscription status for this account." : `This workspace is included with the ${name} plan and above.`}</p>
      {!checking && <Link to="/plans" style={{ display: "inline-flex", minHeight: 36, alignItems: "center", justifyContent: "center", padding: "0 14px", borderRadius: 8, background: palette.accent, color: palette.accentText, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>View plans</Link>}
    </div>
  </div>;
}

/** Each terminal tab fills the space below the navigation bar. */
export default function AppPage() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = useState({ loaded: false, billing_enabled: false, plan: "free" });
  const [visitedSections, setVisitedSections] = useState(() => new Set(["chart"]));

  useEffect(() => {
    let active = true;
    commerceRequest("/billing/status").then((data) => {
      if (active) setBilling({ loaded: true, billing_enabled: Boolean(data.billing_enabled), plan: data.plan || "free" });
    }).catch(() => {
      // Keep existing access if billing cannot be reached; server-backed paid features enforce their own limits.
      if (active) setBilling({ loaded: true, billing_enabled: false, plan: "free" });
    });
    return () => { active = false; };
  }, []);
  const requestedSection = hash.replace(/^#/, "");
  const activeSection = SECTIONS.some((section) => section.id === requestedSection)
    ? requestedSection
    : "chart";

  useEffect(() => {
    setVisitedSections((previous) => {
      if (previous.has(activeSection)) return previous;
      const next = new Set(previous);
      next.add(activeSection);
      return next;
    });
  }, [activeSection]);

  return (
    <div style={{ ...pageStyles.page, height: "100dvh", minHeight: 0, overflow: "hidden" }}>
      <NavBar />
      <main style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {SECTIONS.map((section) => {
          const active = section.id === activeSection;
          const keepMounted = active || visitedSections.has(section.id);
          const requiredPlan = SECTION_PLAN[section.id];
          const checking = Boolean(keepMounted && requiredPlan && !user?.is_admin && !billing.loaded);
          const locked = Boolean(keepMounted && requiredPlan && !user?.is_admin && billing.loaded && billing.billing_enabled && (PLAN_RANK[billing.plan] ?? 0) < PLAN_RANK[requiredPlan]);
          return (
            <section
              key={section.id}
              id={section.id}
              aria-label={section.label}
              aria-hidden={!active}
              style={{ ...tabStyle, display: active ? "flex" : "none" }}
            >
              {checking && <PlanRequired plan={requiredPlan} checking />}
              {locked && <PlanRequired plan={requiredPlan} />}
              {section.id === "chart" && <ChartPage />}
              {section.id === "marketdepth" && keepMounted && !locked && !checking && <MarketDepthPage />}
              {section.id === "python" && keepMounted && !locked && !checking && <PythonPage embedded />}
              {section.id === "backtest" && keepMounted && !locked && !checking && <BacktestPage embedded onOpenEditor={() => navigate("/app#python")} />}
            </section>
          );
        })}
      </main>
    </div>
  );
}
